import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {ObjectType} from 'yougile-model'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {useHover, useTooltipVisible} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {TwCell, TwTooltipText} from './containers'
import {ColumnProps} from './types'
import {useSnappedDeadline} from './use-snapped-deadline'

const TwText = tw.div<{$bold?: boolean; $type?: ObjectType}>`
  whitespace-nowrap
  overflow-hidden
  text-ellipsis
  ${p =>
    cn(
      p.$bold ? 'text-xs-semibold' : 'text-xs-regular',
      p.$type === 'board' ? 'text-primary' : 'text-secondary'
    )}
`

export const Length = observer((props: ColumnProps) => {
  if (!props.item.hubId) {
    return null
  }

  return <LengthBase {...props} />
})

export const LengthBase = observer(
  ({item: {level, hubId, path, type}}: ColumnProps) => {
    const {start, end} = useSnappedDeadline(hubId, path)
    if (!start || !end) {
      return null
    }
    const bold = level <= 0
    const days = Time.weekendAdjustedWorkingDaysBetween(start, end)
    const text = Lang.msg.severalDays(days)

    return <LengthCell text={text} bold={bold} type={type} />
  }
)

export const LengthCell = React.memo(
  ({text, bold, type}: {text: string; bold: boolean; type?: ObjectType}) => {
    const titleRef = useRef<HTMLElement>(null)
    const {hover, onMouseOver, onMouseOut} = useHover()
    const tooltipVisible = useTooltipVisible(titleRef, [text])

    return (
      <TwCell>
        <Tooltip
          disabled={!tooltipVisible || !hover}
          content={<TwTooltipText>{text}</TwTooltipText>}
          offset={[0, 8]}
          placement="bottom"
        >
          <TwText
            ref={titleRef}
            $bold={bold}
            $type={type}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
          >
            {text}
          </TwText>
        </Tooltip>
      </TwCell>
    )
  }
)
